import React from 'react'
import {API_ENDPOINT} from 'keys'
import {globalAuthManager} from 'services/GlobalServices'
import {apiGet, apiPatch} from 'services/api-call-helpers'

export const TARGET_UNIT_FIXED_DOLLAR = 'fixed_dollar'
export const TARGET_UNIT_PERCENT = 'percent'

function formatCurrency(value) {
    if (value == null || value === '') {
        return ''
    }
    const numberValue = Number(value)
    if (Number.isNaN(numberValue)) {
        return ''
    }
    return numberValue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    })
}

export function targetFromAccount(account) {
    const target = account.quarterly_cash_target || {}
    return {
        unit: target.value == null ? TARGET_UNIT_FIXED_DOLLAR : (target.unit || TARGET_UNIT_FIXED_DOLLAR),
        value: target.value == null ? '' : String(target.value),
    }
}

export function draftForUnitChange(unit) {
    return {
        unit,
        value: '',
    }
}

function holdingsTimestampValue(accountHoldings) {
    if (!accountHoldings || !accountHoldings.timestamp) {
        return 0
    }
    const timestamp = Date.parse(accountHoldings.timestamp)
    return Number.isNaN(timestamp) ? 0 : timestamp
}

export function latestNavByAccountId(accountHoldingsObjs) {
    return (accountHoldingsObjs || []).reduce((navByAccountId, accountHoldings) => {
        const accountId = accountHoldings.investment_account
        const holdings = accountHoldings.holdings || {}
        if (!accountId || holdings.NAV == null) {
            return navByAccountId
        }

        const existing = navByAccountId[accountId]
        if (!existing || holdingsTimestampValue(accountHoldings) >= holdingsTimestampValue(existing.accountHoldings)) {
            navByAccountId[accountId] = {
                accountHoldings,
                nav: holdings.NAV,
            }
        }
        return navByAccountId
    }, {})
}

export function accountsWithLatestNav(accounts, accountHoldingsObjs) {
    const navByAccountId = latestNavByAccountId(accountHoldingsObjs)
    return (accounts || []).map(account => ({
        ...account,
        latest_nav: navByAccountId[account.id] ? navByAccountId[account.id].nav : null,
    }))
}

export function accountsWithSettings(accounts, accountSettingsObjs) {
    const settingsByAccountId = (accountSettingsObjs || []).reduce((result, settings) => {
        if (settings.investment_account) {
            result[settings.investment_account] = settings
        }
        return result
    }, {})
    return (accounts || []).map(account => ({
        ...account,
        quarterly_cash_target: settingsByAccountId[account.id] ?
            settingsByAccountId[account.id].quarterly_cash_target :
            null,
    }))
}

export function validateTargetDraft(account, draft) {
    if (draft.value === '') {
        return null
    }

    const value = Number(draft.value)
    if (Number.isNaN(value)) {
        return 'Enter a valid Quarterly cash target.'
    }
    if (value < 0) {
        return 'Quarterly cash target cannot be negative.'
    }
    if (draft.unit === TARGET_UNIT_FIXED_DOLLAR) {
        if (!Number.isInteger(value)) {
            return 'Fixed-dollar targets must be whole dollars.'
        }
        if (account.latest_nav != null && value > Number(account.latest_nav)) {
            return 'Fixed-dollar target cannot exceed account value.'
        }
    } else {
        if (value > 100) {
            return 'Percent targets cannot exceed 100%.'
        }
        if (!Number.isInteger(value * 2)) {
            return 'Percent targets must use 0.5% increments.'
        }
    }
    return null
}

const QuarterlyCashTargetControl = ({account, onSaved}) => {
    const savedTarget = targetFromAccount(account)
    const [draft, setDraft] = React.useState(savedTarget)
    const [isSaving, setIsSaving] = React.useState(false)
    const [error, setError] = React.useState(null)
    const [saved, setSaved] = React.useState(false)

    React.useEffect(() => {
        setDraft(targetFromAccount(account))
        setError(null)
    }, [account.id, account.quarterly_cash_target])

    const isPercent = draft.unit === TARGET_UNIT_PERCENT
    const isDirty = draft.unit !== savedTarget.unit || draft.value !== savedTarget.value

    const handleUnitChange = (event) => {
        setDraft(draftForUnitChange(event.target.value))
        setSaved(false)
        setError(null)
    }

    const handleValueChange = (event) => {
        setDraft({...draft, value: event.target.value})
        setSaved(false)
        setError(null)
    }

    const cancelEdit = () => {
        setDraft(savedTarget)
        setError(null)
    }

    const saveEdit = () => {
        const validationError = validateTargetDraft(account, draft)
        if (validationError) {
            setError(validationError)
            return
        }

        setIsSaving(true)
        setError(null)
        apiPatch(
            API_ENDPOINT.INVESTMENT_ACCOUNT_SETTINGS_DETAIL(account.id),
            {
                quarterly_cash_target: {
                    unit: draft.unit,
                    value: draft.value === '' ? null : draft.value,
                },
            },
            globalAuthManager,
        ).then((response) => {
            const target = response.data.quarterly_cash_target || {}
            const responseTarget = {
                unit: target.value == null ? TARGET_UNIT_FIXED_DOLLAR : (target.unit || TARGET_UNIT_FIXED_DOLLAR),
                value: target.value == null ? '' : String(target.value),
            }
            setDraft(responseTarget)
            setSaved(true)
            setTimeout(() => setSaved(false), 1800)
            onSaved(account.id, responseTarget)
        }).catch((err) => {
            const data = err && err.response ? err.response.data : null
            const valueError = data && data.value ? data.value : null
            const detailError = data && data.detail ? data.detail : null
            setError(Array.isArray(valueError) ? valueError[0] : (valueError || detailError || 'Could not save target.'))
        }).finally(() => setIsSaving(false))
    }

    return (
        <div className="d-flex align-items-center flex-wrap" style={{gap: '0.5rem'}}>
            <div className="input-group input-group-sm" style={{width: 180}}>
                <input
                    className="form-control"
                    type="number"
                    min="0"
                    step={isPercent ? '0.5' : '1'}
                    max={isPercent ? '100' : undefined}
                    value={draft.value}
                    onChange={handleValueChange}
                    placeholder={isPercent ? '0.0' : '0'}
                    aria-label="Quarterly cash target"
                />
                <select
                    className="custom-select"
                    style={{flex: '0 0 auto', width: 'auto'}}
                    value={draft.unit}
                    onChange={handleUnitChange}
                    aria-label="Quarterly cash target unit"
                >
                    <option value={TARGET_UNIT_FIXED_DOLLAR}>$</option>
                    <option value={TARGET_UNIT_PERCENT}>%</option>
                </select>
            </div>
            {isDirty ? (
                <React.Fragment>
                    <button
                        className="btn btn-sm btn-primary"
                        type="button"
                        disabled={isSaving}
                        onClick={saveEdit}
                    >
                        Save
                    </button>
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        type="button"
                        disabled={isSaving}
                        onClick={cancelEdit}
                    >
                        Cancel
                    </button>
                </React.Fragment>
            ) : saved ? (
                <span className="text-success small">Saved</span>
            ) : null}
            {error ? <div className="text-danger small w-100">{error}</div> : null}
        </div>
    )
}

const AccountsProfileContainer = () => {
    const [accounts, setAccounts] = React.useState(null)
    const [error, setError] = React.useState(null)

    const refreshAccounts = () => {
        Promise.all([
            apiGet(API_ENDPOINT.USER_INVESTMENT_ACCOUNTS, {}, globalAuthManager),
            apiGet(API_ENDPOINT.ACCOUNT_HOLDINGS, {}, globalAuthManager),
            apiGet(API_ENDPOINT.INVESTMENT_ACCOUNT_SETTINGS, {}, globalAuthManager),
        ])
            .then(([accountsResponse, accountHoldingsResponse, accountSettingsResponse]) => {
                const accountsWithSettingsData = accountsWithSettings(
                    accountsResponse.data || [],
                    accountSettingsResponse.data || [],
                )
                const accountsWithNav = accountsWithLatestNav(
                    accountsWithSettingsData,
                    accountHoldingsResponse.data || [],
                )
                const sortedAccounts = accountsWithNav.slice().sort((a, b) => a.name.localeCompare(b.name))
                setAccounts(sortedAccounts)
                setError(null)
            })
            .catch(() => {
                setAccounts([])
                setError('Accounts are unavailable.')
            })
    }

    React.useEffect(() => {
        refreshAccounts()
    }, [])

    const updateAccountTarget = (accountId, target) => {
        setAccounts((currentAccounts) => (
            (currentAccounts || []).map((account) => (
                account.id === accountId ? {
                    ...account,
                    quarterly_cash_target: {
                        unit: target.unit,
                        value: target.value === '' ? null : target.value,
                    },
                } : account
            ))
        ))
    }

    if (accounts == null) {
        return <div className="spinner-border bg-transparent"/>
    }

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="font-weight-bold text-dark mb-0">
                    Accounts <span className="text-muted">({accounts.length})</span>
                </h2>
            </div>
            <div className="card-body p-0">
                {error ? <div className="alert alert-danger m-3">{error}</div> : null}
                <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                        <thead>
                        <tr>
                            <th>Account</th>
                            <th className="text-right">Most recent NAV</th>
                            <th className="text-nowrap">
                                Quarterly cash target
                                <span
                                    className="fe fe-info ml-2 text-muted"
                                    title="Cash amount or percent to target at the start of each quarter."
                                    aria-label="Cash amount or percent to target at the start of each quarter."
                                />
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {accounts.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="text-center text-muted py-4">
                                    No accounts yet.
                                </td>
                            </tr>
                        ) : accounts.map(account => (
                            <tr key={account.id}>
                                <td className="align-middle">{account.name}</td>
                                <td className="align-middle text-right">{formatCurrency(account.latest_nav)}</td>
                                <td className="align-middle">
                                    <QuarterlyCashTargetControl account={account} onSaved={updateAccountTarget}/>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default AccountsProfileContainer
